import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

type Hit = {
  score: number;
  recordingId?: string;
  chunkIndex?: number;
  text?: string;
  approxStartSec?: number;
  recordingDateUtc?: string;
  recordingWhen?: string;
};

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  hits?: Hit[];
  isError?: boolean;
};

function scrollToBottom(el: HTMLElement | null) {
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}

export default function Search() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom(scrollRef.current);
  }, [messages, busy]);

  async function send() {
    const text = draft.trim();
    if (!text || busy) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    };
    const historyForApi = [...messages, userMsg].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    setMessages((m) => [...m, userMsg]);
    setDraft("");
    setBusy(true);

    const r = await api<{ hits: Hit[]; answer?: string }>("/api/search", {
      method: "POST",
      body: JSON.stringify({ messages: historyForApi }),
    });

    setBusy(false);

    if (!r.ok) {
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: r.error || "Request failed.",
          isError: true,
        },
      ]);
      return;
    }

    const hits = r.data?.hits ?? [];
    const answer = (r.data?.answer ?? "").trim();
    const assistantText =
      answer ||
      (hits.length > 0
        ? "Here are the closest matches in your library — open a recording to hear that moment."
        : "I didn't find anything in your library that matches. Try different words or a wider question.");

    setMessages((m) => [
      ...m,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: assistantText,
        hits: hits.length > 0 ? hits : undefined,
      },
    ]);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <>
      <div className="search-chat-header">
        <h1 style={{ margin: 0 }}>Search your log</h1>
        {messages.length > 0 ? (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => setMessages([])}
          >
            Clear chat
          </button>
        ) : null}
      </div>
      <p className="muted">
        Chat with an assistant that only sees <strong>your</strong> transcript excerpts retrieved for
        each message. Press <kbd className="search-kbd">Enter</kbd> to send,{" "}
        <kbd className="search-kbd">Shift+Enter</kbd> for a new line.
      </p>

      <details className="card search-privacy">
        <summary>Privacy &amp; your data</summary>
        <div className="search-privacy__body">
          <p>
            Search and optional answers use only <strong>your</strong> saved transcripts and related
            metadata. Each message runs a fresh retrieval from your library; nothing here reads other
            people&apos;s accounts or the open web for this feature.
          </p>
          <p className="muted" style={{ marginBottom: 0 }}>
            Your questions and any generated replies are processed so the feature can respond.
            Retention, how underlying models treat that content, and what you can export or delete are
            covered in our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy</Link>{" "}
            pages. For legal or compliance questions (including third‑party model providers), rely
            on those documents and any notices linked there.
          </p>
        </div>
      </details>

      <div className="card search-chat">
        <div className="search-chat__window" ref={scrollRef} aria-live="polite">
          {messages.length === 0 ? (
            <div className="search-chat__empty muted">
              <p style={{ marginTop: 0 }}>
                Ask anything about what you&apos;ve recorded — for example:{" "}
                <em>What did I mention about work?</em> or <em>Summarize last week&apos;s entries.</em>
              </p>
              <p>Each reply uses fresh search over your library, plus the conversation so far.</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`search-chat__row search-chat__row--${msg.role}${msg.isError ? " search-chat__row--error" : ""}`}
              >
                <div className="search-chat__bubble">
                  <span className="search-chat__label">
                    {msg.role === "user" ? "You" : "Assistant"}
                  </span>
                  <div className="search-chat__text">{msg.content}</div>
                  {msg.role === "assistant" && msg.hits && msg.hits.length > 0 ? (
                    <details className="search-chat__sources">
                      <summary>Sources ({msg.hits.length})</summary>
                      <ul className="search-chat__source-list">
                        {msg.hits.map((h, i) => (
                          <li key={i} className="search-chat__source-item">
                            <span className="muted" style={{ fontSize: "0.8rem" }}>
                              {(h.score * 100).toFixed(0)}% match
                              {h.recordingWhen || h.recordingDateUtc ? (
                                <>
                                  {" · "}
                                  {h.recordingWhen ||
                                    (h.recordingDateUtc
                                      ? new Date(h.recordingDateUtc).toLocaleString()
                                      : "")}
                                </>
                              ) : null}
                              {h.recordingId ? (
                                <>
                                  {" · "}
                                  <Link
                                    to={`/entry/${h.recordingId}${h.approxStartSec != null ? `?t=${Math.floor(h.approxStartSec)}` : ""}`}
                                  >
                                    Open
                                    {h.approxStartSec != null
                                      ? ` ~${Math.floor(h.approxStartSec)}s`
                                      : ""}
                                  </Link>
                                </>
                              ) : null}
                            </span>
                            <p style={{ margin: "0.25rem 0 0" }}>{h.text}</p>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </div>
            ))
          )}
          {busy ? (
            <div className="search-chat__row search-chat__row--assistant">
              <div className="search-chat__bubble search-chat__bubble--typing">
                <span className="search-chat__label">Assistant</span>
                <span className="muted">Searching your library…</span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="search-chat__composer">
          <textarea
            className="search-chat__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about your logs…"
            rows={2}
            disabled={busy}
            aria-label="Message"
          />
          <button
            type="button"
            className="btn btn-primary search-chat__send"
            disabled={busy || !draft.trim()}
            onClick={() => void send()}
          >
            Send
          </button>
        </div>
      </div>

    </>
  );
}
